import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemMedia,
  ItemTitle,
  RadioGroup,
  RadioGroupItem,
} from '@monksflow/monks-ui';
import { useId } from 'react';
import type { RadioCardGroupProps } from './RadioCardGroup.types';

export function RadioCardGroup<T extends string>(props: RadioCardGroupProps<T>) {
  const {
    value,
    onChange,
    options,
    disabled = false,
    className,
    'aria-labelledby': ariaLabelledBy,
    'aria-describedby': ariaDescribedBy,
  } = props;

  const baseId = useId();

  return (
    <RadioGroup
      // Radix treats an undefined value as uncontrolled, so a null selection has
      // to reach it as a value no option can match.
      value={value ?? ''}
      onValueChange={(next) => onChange(next as T)}
      disabled={disabled}
      className={className}
      aria-labelledby={ariaLabelledBy}
      aria-describedby={ariaDescribedBy}
    >
      {options.map((option) => {
        const OptionIcon = option.icon;
        const titleId = `${baseId}-${option.value}-title`;
        const descriptionId = `${baseId}-${option.value}-description`;

        return (
          <Item
            key={option.value}
            variant={value === option.value ? 'selected' : 'outline'}
            // The radio is a button, which a wrapping label cannot activate, so the
            // card carries the pointer target itself.
            onClick={disabled ? undefined : () => onChange(option.value)}
            className={disabled ? 'cursor-not-allowed opacity-40' : 'cursor-pointer'}
          >
            <ItemMedia variant="icon">
              <OptionIcon />
            </ItemMedia>
            <ItemContent>
              <ItemTitle id={titleId}>{option.label}</ItemTitle>
              <ItemDescription id={descriptionId}>{option.description}</ItemDescription>
            </ItemContent>
            <ItemActions>
              <RadioGroupItem
                value={option.value}
                aria-labelledby={titleId}
                aria-describedby={descriptionId}
                // Radix still selects on click; this only keeps the card handler
                // above from firing a second, redundant change.
                onClick={(event) => event.stopPropagation()}
              />
            </ItemActions>
          </Item>
        );
      })}
    </RadioGroup>
  );
}
